// Panier latéral : s'ouvre immédiatement, puis remplace son contenu avec le HTML renvoyé par Shopify.
// Un seul aller-retour : cart/add.js et cart/change.js renvoient déjà la section rendue (paramètre `sections`).
// API globale : window.charmsCart.open() / add([{ id, quantity }]) / refresh() — le <dialog> est dans le DOM dès le chargement.
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const SECTION = 'cart-drawer';
  const CLOSE_MS = 220; // = durée d'animation dans cart-drawer.liquid
  const drawer = () => document.querySelector('[data-cart-drawer]');
  const onCartPage = () => {
    const url = drawer()?.dataset.cartUrl;
    return url && window.location.pathname.replace(/\/$/, '') === url.replace(/\/$/, '');
  };
  const json = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const errorText = (data, fallback) => data?.description || data?.message || fallback;

  const QTY_DEBOUNCE_MS = 300; // +/− rapides : on cumule la quantité visée puis on envoie une seule requête
  let lastHtml = '';
  let loads = 0; // requêtes en cours : le compteur évite qu'une requête n'éteigne l'état de chargement d'une autre
  const pendingQty = new Map(); // clé de ligne -> quantité visée, en attente d'envoi
  let qtyTimer = 0;

  const setCount = (n) => document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = n; });
  const setMsg = (text) => { const el = drawer()?.querySelector('[data-cdr-msg]'); if (el) el.textContent = text || ''; };
  const setLoading = (on) => {
    loads = Math.max(0, loads + (on ? 1 : -1));
    drawer()?.classList.toggle('is-loading', loads > 0);
  };
  const parse = (html) => (html ? new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]') : null);

  // Remplace uniquement le contenu interne : le <dialog> reste en place (donc ouvert, sans saut).
  function show(html, msg) {
    const dlg = drawer();
    const fresh = parse(html);
    if (!dlg || !fresh) return false;
    if (html !== lastHtml) {
      const top = dlg.querySelector('.cdr__scroll')?.scrollTop || 0;
      dlg.innerHTML = fresh.innerHTML;
      dlg.dataset.count = fresh.dataset.count;
      const scroll = dlg.querySelector('.cdr__scroll');
      if (scroll) scroll.scrollTop = top;
      lastHtml = html;
    }
    dlg.classList.remove('is-stale');
    setCount(fresh.dataset.count);
    setMsg(msg);
    return true;
  }

  async function fetchHtml() {
    const res = await fetch(`${root}?sections=${SECTION}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('render');
    return (await res.json())[SECTION];
  }

  // Le chargement masque les totaux : le client ne voit jamais un récapitulatif périmé.
  async function refresh(msg) {
    setLoading(true);
    try {
      return show(await fetchHtml(), msg);
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }

  function open() {
    const dlg = drawer();
    if (!dlg) return false;
    dlg.classList.remove('is-closing');
    if (!dlg.open) dlg.showModal();
    return true;
  }

  function close() {
    const dlg = drawer();
    if (!dlg?.open || dlg.classList.contains('is-closing')) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) { dlg.close(); return; }
    dlg.classList.add('is-closing');
    setTimeout(() => { dlg.close(); dlg.classList.remove('is-closing'); }, CLOSE_MS);
  }

  // POST JSON avec rendu de la section dans la même réponse.
  async function post(path, body) {
    setLoading(true);
    try {
      const res = await fetch(root + path, {
        method: 'POST',
        headers: json,
        body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname }),
      });
      const data = await res.json().catch(() => ({}));
      return { ok: res.ok, data };
    } finally {
      setLoading(false);
    }
  }

  // Affiche le résultat d'un POST : section fournie si succès, sinon rechargement + message d'erreur.
  const showResult = ({ ok, data }, errorMsg) =>
    ok && data.sections?.[SECTION] ? show(data.sections[SECTION], '') : refresh(ok ? '' : errorText(data, errorMsg));

  // Ajout direct : ouverture synchrone, puis UN POST qui renvoie aussi la section rendue.
  async function add(items) {
    open();
    setMsg('');
    setLoading(true);
    let res, data;
    try {
      res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: json,
        body: JSON.stringify({ items, sections: SECTION, sections_url: window.location.pathname }),
      });
      data = await res.json().catch(() => ({}));
    } catch {
      res = { ok: false };
      data = {};
    }
    try {
      if (!res.ok) {
        const message = errorText(data, "Impossible d'ajouter cet article.");
        setMsg(message);
        throw new Error(message);
      }
      if (!show(data.sections?.[SECTION], '')) await refresh();
      return data;
    } finally {
      setLoading(false);
    }
  }

  window.charmsCart = { open, add, refresh: async () => { await refresh(); } };

  // 0. Préchargement au repos : la première ouverture affiche déjà le vrai contenu.
  (window.requestIdleCallback || setTimeout)(async () => {
    try {
      const dlg = drawer();
      const html = await fetchHtml();
      if (!dlg || dlg.open) return;
      if (parse(html)?.dataset.count === dlg.dataset.count) lastHtml = html; else show(html);
    } catch { /* le contenu rendu avec la page fait foi */ }
  });

  // 1. Ajout rapide (charms.js) : le panier est déjà à jour côté serveur, on ouvre puis on recharge.
  document.addEventListener('cart:added', async () => {
    if (onCartPage() || !open()) return;
    await refresh();
  });

  // 2. Formulaire produit principal (le bouton de la barre collante utilise form="pdp-form").
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    // getAttribute : le formulaire contient un champ name="id", qui masque form.id.
    if (form.getAttribute('id') !== 'pdp-form' || !drawer() || onCartPage()) return;
    e.preventDefault();
    const buttons = document.querySelectorAll('#pdp-form [type="submit"], [form="pdp-form"]');
    buttons.forEach((b) => { b.disabled = true; });
    open();
    setLoading(true);
    try {
      const body = new FormData(form);
      body.append('sections', SECTION);
      body.append('sections_url', window.location.pathname);
      const res = await fetch(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body });
      const data = await res.json().catch(() => ({}));
      const shown = await showResult({ ok: res.ok, data }, "Impossible d'ajouter cet article.");
      if (shown) return;
      if (res.ok) location.href = drawer().dataset.cartUrl; // panier lateral indisponible : page panier
      else form.submit();
    } catch {
      form.submit();
    } finally {
      setLoading(false);
      buttons.forEach((b) => { b.disabled = false; });
    }
  });

  // 3. Icône panier de l'en-tête : ouverture immédiate, contenu revérifié en arrière-plan.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a');
    if (!link || !link.querySelector('[data-cart-count]') || e.metaKey || e.ctrlKey || e.shiftKey || onCartPage() || !drawer()) return;
    e.preventDefault();
    if (open()) refresh(); else location.href = link.href;
  });

  // Envoie les quantités cumulées (une requête par ligne modifiée ; la dernière réponse rend l'état final).
  async function flushQty() {
    qtyTimer = 0;
    const jobs = [...pendingQty];
    pendingQty.clear();
    if (!jobs.length) return;
    drawer()?.querySelectorAll('[data-cdr-change]').forEach((b) => { b.disabled = true; });
    setLoading(true);
    try {
      let result;
      for (const [id, quantity] of jobs) {
        result = await post('cart/change.js', { id, quantity });
        if (!result.ok) break;
      }
      if (!result.ok) lastHtml = ''; // annule l'affichage optimiste des quantités
      if (!(await showResult(result, 'Impossible de modifier la quantité.'))) location.reload();
    } finally {
      setLoading(false);
    }
  }

  // + / − : affichage immédiat de la quantité visée, envoi groupé après une courte pause. Retirer : envoi immédiat.
  function queueQty(button) {
    const key = button.dataset.cdrChange;
    const line = button.closest('[data-line-key]');
    const valEl = line?.querySelector('.cdr__qval');
    const step = Number(button.dataset.step);
    if (!step) {
      line?.classList.add('is-removing'); // disparait tout de suite ; le rendu suivant remet l'ensemble en tete
      pendingQty.set(key, 0);
      clearTimeout(qtyTimer);
      flushQty();
      return;
    }
    const target = Math.max(1, (pendingQty.get(key) ?? (Number(valEl?.textContent) || 1)) + step);
    pendingQty.set(key, target);
    if (valEl) valEl.textContent = target;
    const minus = line?.querySelector('[data-step="-1"]');
    if (minus) minus.disabled = target < 2;
    drawer().classList.add('is-stale');
    clearTimeout(qtyTimer);
    qtyTimer = setTimeout(flushQty, QTY_DEBOUNCE_MS);
  }

  // 4. Actions dans le panier latéral (délégation : le contenu est remplacé à chaque rendu).
  document.addEventListener('click', async (e) => {
    const dlg = drawer();
    if (!dlg || (!dlg.contains(e.target) && e.target !== dlg)) return;

    // Fond assombri : la cible est le <dialog> lui-même, mais un clic dans le panneau (ex. sur une zone sans
    // enfant pendant un chargement) ne doit jamais fermer : on ne ferme que si le clic est hors du cadre.
    if (e.target === dlg) {
      const r = dlg.getBoundingClientRect();
      const outside = e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
      if (outside) close();
      return;
    }
    if (e.target.closest('[data-cdr-close]')) { close(); return; }

    const change = e.target.closest('[data-cdr-change]');
    if (change) {
      if (!change.disabled && !dlg.classList.contains('is-loading')) queueQty(change);
      return;
    }
    if (dlg.classList.contains('is-loading')) return;

    const add = e.target.closest('[data-drawer-add]');
    if (add) {
      add.disabled = true;
      try {
        await window.charmsCart.add([{ id: Number(add.dataset.drawerAdd), quantity: 1 }]);
      } catch {
        add.disabled = false; // le message d'erreur est déjà affiché dans le panier
      }
    }
  });

  // Échap : fermeture avec l'animation.
  document.addEventListener('cancel', (e) => {
    if (e.target === drawer()) { e.preventDefault(); close(); }
  }, true);
  // Code promo (panier lateral) : un code a la fois, applique via cart/update.js.
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest?.('[data-cdr-promo]');
    if (!form) return;
    e.preventDefault();
    const code = form.querySelector('input').value.trim();
    if (!code) return;
    showResult(await post('cart/update.js', { discount: code }), "Ce code n'est pas valable.");
  });
  document.addEventListener('click', async (e) => {
    if (!e.target.closest?.('[data-cdr-promo-remove]')) return;
    showResult(await post('cart/update.js', { discount: '' }), '');
  });
})();
